import { Helmet } from 'react-helmet-async';
import { type MouseEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Container,
	FormControl,
	IconButton,
	InputLabel,
	LinearProgress,
	MenuItem,
	Select,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import Iconify from '../../components/iconify';
import { BreadCrumbs } from '../../components/nav-section';
import { SortableList } from '../../components/sortableList';
import { useConfirm } from '../../hooks/useConfirm';
import { useToast } from '../../hooks/useToast';
import type { QuestionnaireSectionProps } from '../../models';
import {
	useGetQuestionnaireQuery,
	useUpdateQuestionnaireMutation,
	useAddSectionToQuestionnaireMutation,
	useRemoveSectionFromQuestionnaireMutation,
	useReorderQuestionnaireSectionsMutation,
} from '../../slices/apis/questionnaireMgmtApi';
import { useGetSectionsQuery } from '../../slices/apis/sectionApi';

export default function QuestionnaireEdit() {
	const { questionnaireId } = useParams() as { questionnaireId: string };
	const navigate = useNavigate();
	const { data, isLoading } = useGetQuestionnaireQuery(Number(questionnaireId));
	const { data: allSections = [] } = useGetSectionsQuery();

	const [updateQuestionnaire, { isLoading: isUpdating }] = useUpdateQuestionnaireMutation();
	const [addSection, { isLoading: isAddingSection }] = useAddSectionToQuestionnaireMutation();
	const [removeSection] = useRemoveSectionFromQuestionnaireMutation();
	const [reorderSections, { isLoading: isReordering }] = useReorderQuestionnaireSectionsMutation();

	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [selectedSectionId, setSelectedSectionId] = useState<number | ''>('');
	const [localSections, setLocalSections] = useState<QuestionnaireSectionProps[]>([]);
	const [sectionToRemove, setSectionToRemove] = useState<QuestionnaireSectionProps | null>(null);
	const { Confirm, confirmProps } = useConfirm();
	const { showToast, ToastElement } = useToast();

	const handleNavigate = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			navigate(e.currentTarget.getAttribute('data-route') as string);
		},
		[navigate],
	);

	useEffect(() => {
		if (data) {
			setName(data.name);
			setDescription(data.description ?? '');
			setLocalSections([...data.sections].sort((a, b) => a.orderIndex - b.orderIndex));
		}
	}, [data]);

	const handleSave = async () => {
		if (!name.trim()) {
			showToast('Name is required.', 'error');
			return;
		}

		try {
			await Promise.all([
				updateQuestionnaire({ id: Number(questionnaireId), name, description: description || null }).unwrap(),
				reorderSections({
					questionnaireId: Number(questionnaireId),
					sections: localSections.map((section, index) => ({ sectionId: section.id, orderIndex: index + 1 })),
				}).unwrap(),
			]);
			showToast('Questionnaire has been updated successfully!', 'success');
		} catch {
			showToast('Failed to save questionnaire. Please try again.', 'error');
		}
	};

	const handleAddSection = async () => {
		if (!selectedSectionId) return;
		try {
			const nextOrder = localSections.length + 1;
			await addSection({
				questionnaireId: Number(questionnaireId),
				sectionId: Number(selectedSectionId),
				orderIndex: nextOrder,
			}).unwrap();
			setSelectedSectionId('');
			showToast('Section has been added successfully!', 'success');
		} catch {
			showToast('Failed to add section.', 'error');
		}
	};

	const handleRemoveSection = async (sectionId: number) => {
		try {
			await removeSection({ questionnaireId: Number(questionnaireId), sectionId }).unwrap();
			showToast('Section has been removed successfully!', 'success');
		} catch {
			showToast('Failed to remove section.', 'error');
		}
	};

	const openRemoveSectionConfirm = (section: QuestionnaireSectionProps) => {
		setSectionToRemove(section);
		confirmProps.openConfirm();
	};

	const assignedSectionIds = new Set(localSections.map((section) => section.id));
	const availableSections = allSections.filter((section: { id: number }) => !assignedSectionIds.has(section.id));
	const breadCrumbItems = [
		{
			text: 'My Questionnaires',
			onClick: handleNavigate,
			dataRoute: '/dashboard/admin/questionnaires',
		},
		{
			text: `Questionnaire #${questionnaireId}`,
		},
	];

	if (isLoading) {
		return (
			<Container maxWidth="md" sx={{ mt: 4 }}>
				<LinearProgress />
			</Container>
		);
	}

	return (
		<>
			<Helmet>
				<title>Edit Questionnaire — {process.env.REACT_APP_ORG}</title>
			</Helmet>
			{ToastElement}
			<Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
				<Card variant="outlined">
					<CardContent>
						<Stack spacing={3}>
							<Box>
								<BreadCrumbs items={breadCrumbItems} />
							</Box>

							<Stack spacing={2}>
								<TextField
									label="Name"
									variant="standard"
									required
									fullWidth
									value={name}
									onChange={(e) => setName(e.target.value)}
								/>
								<TextField
									label="Description"
									variant="standard"
									fullWidth
									multiline
									value={description}
									onChange={(e) => setDescription(e.target.value)}
								/>
							</Stack>

							<Box>
								<Typography variant="h6" gutterBottom>
									Sections
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
									Use section to group related questions together. You can add, remove, and reorder sections as needed.
								</Typography>
								{localSections.length === 0 ? (
									<Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
										No sections assigned yet.
									</Typography>
								) : (
									<SortableList
										items={localSections}
										onChange={setLocalSections}
										renderItem={(section) => (
											<SortableList.Item id={section.id}>
												<Stack direction="row" alignItems="center" spacing={1} sx={{ flexGrow: 1, minWidth: 0 }}>
													<Box sx={{ flexGrow: 1, minWidth: 0 }}>
														<Typography variant="body2" fontWeight={500} noWrap>
															{section.name}
														</Typography>
														{section.description && (
															<Typography variant="caption" color="text.secondary" noWrap>
																{section.description}
															</Typography>
														)}
													</Box>
													<Chip label={`#${section.orderIndex}`} size="small" />

													{/*
													<Tooltip title="Edit section questions">
														<IconButton
															size="small"
															onClick={() => navigate(`/dashboard/admin/sections/${section.id}/edit`)}
														>
															<Iconify icon="eva:edit-fill" width={18} />
														</IconButton>
													</Tooltip>
													*/}

													<Tooltip title="Remove from questionnaire">
														<IconButton size="small" color="error" onClick={() => openRemoveSectionConfirm(section)}>
															<Iconify icon="eva:trash-2-outline" width={18} />
														</IconButton>
													</Tooltip>
												</Stack>
												<SortableList.DragHandle />
											</SortableList.Item>
										)}
									/>
								)}

								<Stack direction="row" spacing={2} mt={3} alignItems="center">
									<FormControl size="small" sx={{ minWidth: 220 }}>
										<InputLabel>Add Section</InputLabel>
										<Select
											value={selectedSectionId}
											label="Add Section"
											onChange={(e) => setSelectedSectionId(Number(e.target.value))}
										>
											{availableSections.map((section: { id: number; name: string }) => (
												<MenuItem key={section.id} value={section.id}>
													{section.name}
												</MenuItem>
											))}
										</Select>
									</FormControl>
									<Button
										variant="outlined"
										startIcon={isAddingSection ? <CircularProgress size={14} /> : <AddIcon />}
										onClick={handleAddSection}
										disabled={!selectedSectionId || isAddingSection}
									>
										Add
									</Button>
								</Stack>
							</Box>

							<Stack direction="row" spacing={4}>
								<Button loading={isUpdating || isReordering} variant="outlined" color="primary" onClick={handleSave}>
									<span>Save</span>
								</Button>
							</Stack>
						</Stack>
					</CardContent>
				</Card>
			</Container>

			<Confirm
				{...confirmProps}
				title="Remove section from questionnaire?"
				onConfirm={async () => {
					if (!sectionToRemove) {
						confirmProps.closeConfirm();
						return;
					}

					confirmProps.closeConfirm();
					await handleRemoveSection(sectionToRemove.id);
					setSectionToRemove(null);
				}}
			>
				<Typography variant="body1">
					Are you sure you want to remove
					{sectionToRemove ? ` "${sectionToRemove.name}"` : ' this section'} from this questionnaire?
				</Typography>
			</Confirm>
		</>
	);
}
